"use client";

import { useRouter } from "next/navigation";
import { useEffect, useId, useRef, useState, useTransition } from "react";
import { EMOJI_REACTIONS } from "@/lib/constants/reactions";
import type { ReactionSummary } from "@/types/database";

/** The emoji panel's height (two rows of 40px, padding), for flipping. */
const PANEL_HEIGHT = 112;

/** Space under the button, inside whatever clips it (a scrolling log, or the window). */
function roomBelow(element: HTMLElement | null): number {
	if (!element) return Number.POSITIVE_INFINITY;
	const bottom = element.getBoundingClientRect().bottom;
	let limit = window.innerHeight;
	for (let node = element.parentElement; node; node = node.parentElement) {
		const { overflowY } = getComputedStyle(node);
		if (
			overflowY === "auto" ||
			overflowY === "scroll" ||
			overflowY === "hidden"
		) {
			limit = Math.min(limit, node.getBoundingClientRect().bottom);
			break;
		}
	}
	return limit - bottom;
}

/**
 * Emoji reactions in the editorial system: the reactions so far as small
 * chips (tap yours to take it back, tap another to add it), then an add
 * button that opens the set. Optimistic; the server action confirms.
 */
export function EmojiReactions({
	label,
	summary: initialSummary = [],
	mine: initialMine = [],
	signedIn,
	onToggle,
}: {
	/** What is being reacted to, for screen readers ("post", "reply"). */
	label: string;
	summary?: ReactionSummary[];
	mine?: string[];
	signedIn: boolean;
	onToggle: (
		emoji: string,
		hasReaction: boolean,
	) => Promise<{ success: boolean }>;
}) {
	const router = useRouter();
	const [summary, setSummary] = useState(initialSummary);
	const [mine, setMine] = useState(initialMine);
	const [open, setOpen] = useState(false);
	// Open upwards when there isn't room below (e.g. the last chat message,
	// just above the message box), measured against the scrolling area.
	const [up, setUp] = useState(false);
	// Take in fresh counts when the page's data changes (others reacting).
	const incoming = JSON.stringify([initialSummary, initialMine]);
	// biome-ignore lint/correctness/useExhaustiveDependencies: compare by value
	useEffect(() => {
		setSummary(initialSummary);
		setMine(initialMine);
	}, [incoming]);
	const [, startTransition] = useTransition();
	const panelId = useId();
	const rootRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);

	// Close on a click outside or Escape.
	useEffect(() => {
		if (!open) return;
		const onPointer = (event: PointerEvent) => {
			if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
		};
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				setOpen(false);
				triggerRef.current?.focus();
			}
		};
		document.addEventListener("pointerdown", onPointer);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("pointerdown", onPointer);
			document.removeEventListener("keydown", onKey);
		};
	}, [open]);

	function toggle(emoji: string) {
		if (!signedIn) {
			router.push(
				`/login?redirectedFrom=${encodeURIComponent(window.location.pathname)}`,
			);
			return;
		}
		const has = mine.includes(emoji);
		const prevSummary = summary;
		const prevMine = mine;
		setMine(has ? mine.filter((item) => item !== emoji) : [...mine, emoji]);
		setSummary((current) => {
			const next = current.map((item) => ({ ...item }));
			const found = next.find((item) => item.reaction_type === emoji);
			if (found) found.count += has ? -1 : 1;
			else if (!has) next.push({ reaction_type: emoji, count: 1 });
			return next.filter((item) => item.count > 0);
		});
		setOpen(false);
		startTransition(async () => {
			const result = await onToggle(emoji, has);
			if (!result.success) {
				setSummary(prevSummary);
				setMine(prevMine);
			}
		});
	}

	const nameFor = (emoji: string) =>
		EMOJI_REACTIONS.find((item) => item.emoji === emoji)?.label ?? emoji;

	return (
		<div ref={rootRef} className="ed-reactions">
			{summary.map((item) => {
				const selected = mine.includes(item.reaction_type);
				return (
					<button
						key={item.reaction_type}
						type="button"
						className="ed-reaction"
						aria-pressed={selected}
						data-mine={selected ? "true" : undefined}
						onClick={() => toggle(item.reaction_type)}
						aria-label={`${nameFor(item.reaction_type)}, ${item.count}${selected ? ", yours" : ""}`}
					>
						<span aria-hidden>{item.reaction_type}</span>
						<span aria-hidden className="ed-reaction__count">
							{item.count}
						</span>
					</button>
				);
			})}
			<button
				ref={triggerRef}
				type="button"
				className="ed-reaction ed-reaction--add"
				aria-expanded={open}
				aria-controls={panelId}
				aria-label={`React to this ${label}`}
				onClick={() => {
					if (!signedIn) {
						toggle("");
						return;
					}
					if (!open) setUp(roomBelow(triggerRef.current) < PANEL_HEIGHT);
					setOpen((value) => !value);
				}}
			>
				<svg
					aria-hidden="true"
					width="1.25em"
					height="1.25em"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth="1.6"
					strokeLinecap="round"
					strokeLinejoin="round"
				>
					<circle cx="11" cy="13" r="8" />
					<path d="M8 15.5c.8 1 1.8 1.5 3 1.5s2.2-.5 3-1.5" />
					<path d="M8.5 10.5h.01M13.5 10.5h.01" />
					<path d="M19 2v6M16 5h6" />
				</svg>
			</button>
			{open ? (
				<fieldset
					id={panelId}
					className="ed-reaction-panel"
					data-up={up ? "true" : undefined}
				>
					<legend className="sr-only">Reactions</legend>
					{EMOJI_REACTIONS.map((item) => (
						<button
							key={item.emoji}
							type="button"
							className="ed-reaction-panel__emoji"
							aria-pressed={mine.includes(item.emoji)}
							data-mine={mine.includes(item.emoji) ? "true" : undefined}
							title={item.label}
							aria-label={item.label}
							onClick={() => toggle(item.emoji)}
						>
							{item.emoji}
						</button>
					))}
				</fieldset>
			) : null}
		</div>
	);
}
